import { useState, useEffect, useCallback } from "react";
import { useOutletContext, Navigate } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar, User, ChevronDown, ChevronUp, Search, Package, Layers, Image, File, ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { activityLogService, type ActivityLog } from "../services/activityLog";

const ALLOWED_EMAILS_ATIVIDADES = [
  'ronald@ronald.com',
  'contato@foradocloset.com.br'
];

const SUBJECT_ICONS: Record<string, React.ReactNode> = {
  "App\\Models\\Product": <Package className="h-4 w-4" />,
  "App\\Models\\Category": <Layers className="h-4 w-4" />,
  "App\\Models\\Banner": <Image className="h-4 w-4" />,
  "App\\Models\\Page": <File className="h-4 w-4" />,
  "App\\Models\\Post": <File className="h-4 w-4" />,
  "App\\Models\\Order": <ShoppingBag className="h-4 w-4" />,
  "App\Models\Order": <ShoppingBag className="h-4 w-4" />,
};

const SUBJECT_LABELS: Record<string, string> = {
  "App\\Models\\Product": "Produto",
  "App\\Models\\Category": "Categoria",
  "App\\Models\\Banner": "Banner",
  "App\\Models\\Page": "Página",
  "App\\Models\\Post": "Post",
  "App\\Models\\Order": "Pedido",
  "App\Models\Order": "Pedido",
};

const parseLocalDate = (dateStr: string) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export function AtividadesPage() {
  const { user } = useOutletContext<{ user: any }>() || {};

  if (!user || !ALLOWED_EMAILS_ATIVIDADES.includes(user.email)) {
    return <Navigate to="/produtos" replace />;
  }

  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState({
    description: "",
    date_from: "",
    date_to: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [dateFromInput, setDateFromInput] = useState("");
  const [dateToInput, setDateToInput] = useState("");

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 50,
        ...(appliedFilters.description && { description: appliedFilters.description }),
        ...(appliedFilters.date_from && { date_from: appliedFilters.date_from }),
        ...(appliedFilters.date_to && { date_to: appliedFilters.date_to }),
      };

      const response = await activityLogService.list(params);
      setActivities(response.data || []);
      setTotalPages(response.last_page || 1);
    } catch (error) {
      console.error("Erro ao buscar activity log:", error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const getDiff = (old: Record<string, any>, newAttr: Record<string, any>) => {
    const diff: Record<string, { old: any; new: any }> = {};
    const allKeys = new Set([
      ...Object.keys(old || {}),
      ...Object.keys(newAttr || {}),
    ]);

    allKeys.forEach((key) => {
      const oldVal = old?.[key];
      const newVal = newAttr?.[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        diff[key] = { old: oldVal, new: newVal };
      }
    });

    return diff;
  };

  const clearFilters = () => {
    setAppliedFilters({ description: "", date_from: "", date_to: "" });
    setSearchInput("");
    setDateFromInput("");
    setDateToInput("");
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setAppliedFilters({
      description: searchInput,
      date_from: dateFromInput,
      date_to: dateToInput,
    });
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const hasActiveFilters =
    appliedFilters.description || appliedFilters.date_from || appliedFilters.date_to;

  const getEventLabel = (description: string) => {
    if (description.includes("created")) return "Criado";
    if (description.includes("updated")) return "Atualizado";
    if (description.includes("deleted")) return "Excluído";
    return description;
  };

  const getEventColor = (description: string) => {
    if (description.includes("created")) return "bg-green-100 text-green-800";
    if (description.includes("updated")) return "bg-blue-100 text-blue-800";
    if (description.includes("deleted")) return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  if (loading && activities.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Carregando atividades...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Activity Log</h1>
        <p className="text-sm md:text-base text-gray-600">
          Acompanhe todas as mudanças realizadas no sistema
        </p>
      </div>

      {/* Loading indicator */}
      {loading && activities.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center gap-2 text-sm text-blue-700">
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          Atualizando resultados...
        </div>
      )}

      {/* Filtros */}
      <Card className="mb-4 md:mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg md:text-xl">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            <input
              type="text"
              placeholder="Buscar..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <input
              type="date"
              value={dateFromInput}
              onChange={(e) => setDateFromInput(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <input
              type="date"
              value={dateToInput}
              onChange={(e) => setDateToInput(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <Button
              onClick={handleSearch}
              variant="outline"
              disabled={loading}
              className="flex items-center justify-center gap-2 h-[38px]"
            >
              <Search className="h-4 w-4" />
              <span>{loading ? "Buscando..." : "Buscar"}</span>
            </Button>

            <Button
              onClick={clearFilters}
              variant="outline"
              className="flex items-center justify-center gap-2 h-[38px]"
              disabled={!hasActiveFilters && !searchInput && !dateFromInput && !dateToInput}
            >
              Limpar
            </Button>
          </div>

          {hasActiveFilters && (
            <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
              <span>Filtros aplicados:</span>
              {appliedFilters.description && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                  Busca: "{appliedFilters.description}"
                </span>
              )}
              {(appliedFilters.date_from || appliedFilters.date_to) && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                  {appliedFilters.date_from && (() => {
                    const d = parseLocalDate(appliedFilters.date_from);
                    return d ? `De: ${format(d, "dd/MM/yyyy")}` : "";
                  })()}
                  {appliedFilters.date_to && (() => {
                    const d = parseLocalDate(appliedFilters.date_to);
                    return d ? ` Até: ${format(d, "dd/MM/yyyy")}` : "";
                  })()}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de Atividades */}
      <div className="space-y-3">
        {activities.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              Nenhuma atividade encontrada
            </CardContent>
          </Card>
        ) : (
          activities.map((activity) => {
            const isExpanded = expandedId === activity.id;
            const properties = activity.properties || {};
            const diff = getDiff(
              properties.old || {},
              properties.attributes || {}
            );

            const subjectLabel = SUBJECT_LABELS[activity.subject_type] || activity.subject_type;
            const subjectIcon = SUBJECT_ICONS[activity.subject_type];

            return (
              <Card key={activity.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-0">
                  {/* Header */}
                  <div
                    className="flex items-center justify-between p-3 md:p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() =>
                      setExpandedId(isExpanded ? null : activity.id)
                    }
                  >
                    <div className="flex-1 min-w-0">
                      {/* Mobile: stack vertical */}
                      <div className="flex flex-col gap-2 sm:hidden">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="text-gray-400 flex-shrink-0">
                              {subjectIcon}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {activity.subject_name || `${subjectLabel} #${activity.subject_id}`}
                              </div>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 text-[10px] rounded font-medium whitespace-nowrap ${getEventColor(activity.description)}`}>
                            {getEventLabel(activity.description)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>
                              {format(
                                new Date(activity.created_at),
                                "dd/MM/yyyy HH:mm",
                                { locale: ptBR }
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="truncate max-w-[120px]">
                              {activity.user_responsible?.display_name || activity.causer_name || "Sistema"}
                            </span>
                            {activity.user_responsible?.is_admin && (
                              <span className="px-1 py-0.5 text-[9px] font-medium bg-purple-100 text-purple-700 rounded">
                                Admin
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Desktop: horizontal */}
                      <div className="hidden sm:flex items-center gap-3 md:gap-4">
                        {/* Data/Hora */}
                        <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600 flex-shrink-0">
                          <Calendar className="h-3 w-3 md:h-4 md:w-4" />
                          <span className="whitespace-nowrap">
                            {format(
                              new Date(activity.created_at),
                              "dd/MM/yyyy HH:mm",
                              { locale: ptBR }
                            )}
                          </span>
                        </div>

                        {/* Usuário */}
                        <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-600 min-w-0">
                          <User className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
                          <span className="truncate">
                            {activity.user_responsible?.display_name || activity.causer_name || "Sistema"}
                          </span>
                          {activity.user_responsible?.is_admin && (
                            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded flex-shrink-0">
                              Admin
                            </span>
                          )}
                        </div>

                        {/* Evento */}
                        <div className="flex-shrink-0">
                          <span
                            className={`px-2 py-1 text-xs rounded font-medium whitespace-nowrap ${getEventColor(activity.description)}`}
                          >
                            {getEventLabel(activity.description)}
                          </span>
                        </div>

                        {/* Subject */}
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-gray-400 flex-shrink-0">
                            {subjectIcon}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {activity.subject_name || `${subjectLabel} #${activity.subject_id}`}
                            </div>
                            {activity.subject_sku && (
                              <div className="text-xs text-gray-500 font-mono truncate">
                                SKU: {activity.subject_sku}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Ícone expandir */}
                    <div className="text-gray-400 flex-shrink-0 ml-2 md:ml-4">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                    </div>
                  </div>

                  {/* Detalhes expandidos */}
                  {isExpanded && (
                    <div className="border-t bg-gray-50 p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs text-gray-500">
                          {subjectLabel} ID: {activity.subject_id}
                        </span>
                      </div>

                      <h3 className="text-sm font-semibold mb-3">
                        Mudanças Realizadas
                      </h3>

                      {Object.keys(diff).length === 0 ? (
                        <p className="text-sm text-gray-400">
                          Sem mudanças visíveis
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {Object.entries(diff).map(([key, value]) => (
                            <div
                              key={key}
                              className="bg-white rounded border p-3"
                            >
                              <div className="font-medium text-sm mb-2 capitalize">
                                {key.replace(/_/g, " ")}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                  <div className="text-red-600 mb-1 font-medium">
                                    Antes:
                                  </div>
                                  <div className="bg-red-50 p-2 rounded border border-red-200 break-all font-mono text-[11px]">
                                    {typeof value.old === "object"
                                      ? JSON.stringify(value.old, null, 2)
                                      : String(value.old ?? "null")}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-green-600 mb-1 font-medium">
                                    Depois:
                                  </div>
                                  <div className="bg-green-50 p-2 rounded border border-green-200 break-all font-mono text-[11px]">
                                    {typeof value.new === "object"
                                      ? JSON.stringify(value.new, null, 2)
                                      : String(value.new ?? "null")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <Button
            variant="outline"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            className="px-2 md:px-4"
          >
            <ChevronLeft className="h-4 w-4 sm:hidden" />
            <span className="hidden sm:inline">Anterior</span>
          </Button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                onClick={() => setCurrentPage(pageNum)}
                className="w-9 h-9 md:w-10 md:h-10 text-sm"
              >
                {pageNum}
              </Button>
            );
          })}

          <span className="text-xs md:text-sm text-gray-600 mx-2">
            de {totalPages}
          </span>

          <Button
            variant="outline"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            className="px-2 md:px-4"
          >
            <ChevronRight className="h-4 w-4 sm:hidden" />
            <span className="hidden sm:inline">Próxima</span>
          </Button>
        </div>
      )}
    </div>
  );
}
